import * as React from 'react';

import { ClockingUserDTO, GetDeviceConfigurationResponse, Res } from 'core/api';
import { Result } from 'core/infrastructure';
import { Command } from 'pos/common';
import { Content, Header, PosScreen, UserButton } from 'pos/ui';
import * as Images from 'pos/Images';
import { AttendanceFacade, RestaurantFacade } from 'pos/facades';
import { registerClockingData } from './registerClockingData';
import { confirmPasswordForClocking } from './confirmPasswordForClocking';
import { ReaderStatus } from 'pos/common/BridgeProxy.Endpoints';
import { Message, Modal } from 'core/ui-next/modal';
import { Toaster } from 'core/ui-next';
import { Grid, HStack, Text, VStack } from '@chakra-ui/react';
import { useFingerprintReaderRef } from 'pos/services/fingerprint';
import { useBarcodeReaderRef } from 'core/infrastructure-next';

declare global {
	interface CommandType {
		doClocking: () => Promise<Result>
	}
}

type Props = {
	users: ClockingUserDTO[],
	configuration: GetDeviceConfigurationResponse
} & Modal.Actions<{ user: ClockingUserDTO, requirePassword: boolean }>;



function SelectUserForClockingScreen(props: Props) {

	const { cancel, close, users } = props;

	const screenRef = React.useRef<HTMLDivElement>(null);

	const onBarcodeRead = async (barcode: string) => {

		const user = await AttendanceFacade.getUserByCardNumber(barcode);
		if (user.Id === 0) {
			await Message.show(Res.Msg.MSG_INVALID_USER_CODE());
			return;
		}
		if (!user.CanClockingWithCardNumber) {
			await Message.show(Res.Msg.MSG_CANNOT_REGISTER_CLOCKING_WITH_CARDNUMBER());
			return;
		}

		close({ user, requirePassword: false });
	};

	useBarcodeReaderRef(onBarcodeRead);

	const onFingerprintRead = React.useCallback(async (status: ReaderStatus) => {

		if (status.failure)
			Toaster.error(status.failure);
		if (status.verifiedUserId != null) {
			const allUsers = await AttendanceFacade.getUsers();
			const user = allUsers.find(x => x.Id === status.verifiedUserId);
			if (user)
				close({ user, requirePassword: false });
		}
	}, [close]);

	const onUserSelect = async (user: ClockingUserDTO) => {
		close({ user, requirePassword: true });
	};

	useFingerprintReaderRef(onFingerprintRead);

	return <PosScreen screenId='SELECT_USER_FOR_CLOCKING' ref={screenRef} size={'medium'}>

		<Header title={Res.Web.SELECT_USER_FOR_CLOCKING()} image={Images.Clocking} onClose={cancel}></Header>

		<Content>
			<VStack width={'full'} height={'full'}>
				<HStack w='full' justifyContent={'center'} mt='6'>
					<Text fontSize={'md'} fontWeight={'bold'}>{Res.Web.SELECT_USER_FOR_CLOCKING_TITLE()}</Text>
				</HStack>

				<VStack justifyContent={'center'} w='full' mt='4' gap='0'>
					<Text fontSize='sm' fontWeight={'semibold'}>{Res.Web.SELECT_USER_FOR_CLOCKING_USER_FINGERPRINT_LBL()}</Text>
					<Text fontSize='sm' fontWeight={'semibold'}>{Res.Web.SELECT_USER_FOR_CLOCKING_SELECT_USER_LBL()}</Text>
				</VStack>

				<Grid width={'full'} gap={'6'} paddingY={'9'} paddingX={'12'} templateColumns='repeat(auto-fill, 6rem)' overflow={'auto'}>
					{
						users.map(user => (
							<UserButton key={user.Id} user={user} onSelect={onUserSelect} />
						))
					}
				</Grid>
			</VStack>
		</Content>

	</PosScreen>;
}


Command.register('doClocking', 'NO_PRECONDITION', async (): Promise<Result> => {
	const [users, configuration] = await Promise.all([
		AttendanceFacade.getUsers(),
		RestaurantFacade.getDeviceConfiguration()
	]);

	const result = await Modal.show(SelectUserForClockingScreen, { users, configuration });
	if (result.ok) {
		const { user, requirePassword } = result.value;
		const ok = requirePassword ? await confirmPasswordForClocking(user) : true;
		if (ok)
			await registerClockingData(user);
	}

	return Result.ok();
});

